/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/

import { inject, Injectable } from "@angular/core";
import { forkJoin, Observable, of } from "rxjs";
import { first, map } from "rxjs/operators";
import { AbstractObjectList, uuid } from "@src/app/model/abstract-object";
import { User } from "@src/app/model/user";
import { CurrentUserService } from "@src/app/modules/user/current-user.service";
import { BatchType, ImportParser } from '@src/app/modules/importer/import-page/Import-parser-interface';
import { ImportDoc } from "@src/app/modules/importer/ImportDoc";
import { GeneralParser } from "@src/app/modules/importer/General/GeneralParserService";
import { ParseOptionBase, ParseValidator } from "@src/app/modules/importer/General/ParseOptionBase";
import { ParseOptionBaseMapped } from "@src/app/modules/importer/General/ParseOptionBaseMapped";
import { POBValidators, requiredNumber, validateDatePOB } from "@src/app/modules/importer/General/POBValidators";
import { UnitService } from "@src/app/modules/unit/unit.service";
import { ImportField } from "@src/app/modules/importer/ImportField";
import { BCodeService } from "@src/app/modules/budget/bcode.service";
import { ScheduleService } from "@src/app/modules/budget/schedule.service";
import { PeriodService } from "@src/app/modules/budget/period.service";
import { Period } from "@src/app/model/period";
import { MyInjector } from "@src/app/app.module";
import { SupplierService } from "@src/app/modules/supply/supplier.service";
import { ParseOptionBaseBCode } from "@src/app/modules/importer/General/ParseOptionBaseBCode";
import { BCode } from "@src/app/model/bcode";
import { ApTxnService } from "@src/app/modules/txn/ap-txn.service";
import { ImportRow } from "@src/app/modules/importer/ImportRow";
import { TenantService } from '@src/app/shared/tenant.service';
import { HttpClient, HttpParams } from "@angular/common/http";
import { SupplierPageComponent } from "@src/app/modules/supply/supplier-page/supplier-page.component";
import { BankAccountService } from "@src/app/modules/txn/bank-account.service";
import { BankAccount } from "@src/app/model/bankAccount";
import { PurchaseService } from "@src/app/modules/txn/purchase.service";
import { Txn } from "@src/app/model/txn";
import { NrHelper } from "@src/app/shared/NrHelper";

type TypeId ='Purchase'|'PurchaseCredit'|'Payment';

interface ItemInput {
    supplierId: string;
    typeId: TypeId;
    txnDate: string;
    reference: string;
    lineAmount: number;
    bCodeId: string;
    notes: string;
    scheduleId: string;
    svcFrom: string;
    svcTo: string;
}

interface LineItem {
    bCodeId: string;
    scheduleId: string;
    debit?: number;
    credit?: number;
    notes: string;
}

interface PurchaseOutput {
    supplierId: string;
    //typeId: TypeId;
    txnDate: string;
    notes: string;
    credit: number;
    reference: string;
    svcFrom: string;
    svcTo: string;
    lineItems: LineItem[];
    outstanding: number; // Used for validation only, not directly posted...
}

interface CreditOutput {
    supplierId: string;
    //typeId: TypeId;
    txnDate: string;
    notes: string;
    debit: number;
    reference: string;
    lineItems: LineItem[];
    outstanding: number;
    svcFrom: string;
    svcTo: string;
}

interface PaymentOutput {
    supplierId: string;
    //typeId: TypeId;
    txnDate: string;
    notes: string;
    reference: string;
    amount: number;
    bankId: uuid;
}

interface ApImports {
    purchases: PurchaseOutput[],
    credits: CreditOutput[],
    payments: PaymentOutput[];
}

@Injectable({
    providedIn: 'root'
})
export class ApParserService implements ImportParser {

    gps = new GeneralParser();
    unitSvc = inject(UnitService);
    currentUserSvc = inject(CurrentUserService);
    bCodeSvc = inject(BCodeService);
    scheduleSvc = inject(ScheduleService);
    apSvc = inject(ApTxnService);
    periodSvc = inject(PeriodService);
    bankAccountSvc = inject(BankAccountService);
    bankAccounts: BankAccount[] = [];
    purchaseSvc = inject(PurchaseService);

    batchType: BatchType = 'ApImport';

    periods : Period[] = [];
    outstanding: Txn[] = [];

    importDoc: ImportDoc;
    importPurchases = new Map<string, PurchaseOutput>();
    importCredits = new Map<string, CreditOutput>();
    importPayments: PaymentOutput[] = [];
    currentUser: User;

    supplierNamePob = new ParseOptionBaseMapped('Supplier', 'supplierId', [POBValidators.requiredPOB()]).setCreator(SupplierPageComponent);

    txnDatePOB = new ParseOptionBase('Transaction Date', 'txnDate')
        .addValidator(validateDatePOB())
        .addValidator({validateRow: this.validatePeriodOpen.bind(this)});

    svcFromPOB = new ParseOptionBase('Service From', 'svcFrom')
        .addValidator(validateDatePOB(true))

    svcToPOB = new ParseOptionBase('Service To', 'svcTo')
        .addValidator(validateDatePOB(true))

    notesPOB = new ParseOptionBase('Note', 'notes');

    bCodePOB = new ParseOptionBaseBCode('Budget Code', 'bCodeId', [POBValidators.requiredPOB()]);
    bCodes: BCode[] = [];
    schedulePOB = new ParseOptionBaseMapped('Schedule', 'scheduleId');
    amountPOB = new ParseOptionBase('Amount', 'lineAmount').addValidator(requiredNumber);
    typeValidator = {
        validateRow: (field: ImportField) => {
            const val = field.getValue();
            if (val === 'Purchase' || val === 'PurchaseCredit' || val === 'Payment') {
                return null;
            }
            return $localize `Invalid type ${val} - Expected Purchase, PurchaseCredit or Payment`;
        }
    } as ParseValidator
    typePOB = new ParseOptionBase('Type', 'typeId').addValidator(this.typeValidator);

    referenceValidator = {
        validateRow: (field: ImportField) => {
            const val = field.getValue();
            if (!val || String(val).length > 128) {
                return $localize `Reference is required, cannot be more than 128 characters (prefer < 30)`;
            }
            return null;
        }
    } as ParseValidator
    referencePOB = new ParseOptionBase('Supplier Reference', 'reference',[this.referenceValidator]);

    availableImportItems = [
        this.supplierNamePob,
        this.typePOB,
        this.txnDatePOB,
        this.referencePOB,
        this.notesPOB,
        this.bCodePOB,
        this.schedulePOB,
        this.amountPOB,
        this.svcFromPOB,
        this.svcToPOB
    ];

    setUp() {
        this.importDoc = new ImportDoc();

        return forkJoin({
            currentUser: this.currentUserSvc.getCurrentUser().pipe(first()),
            suppliers: MyInjector.instance.get(SupplierService).get(true),
            bCodes: this.bCodeSvc.get<BCode>(true),
            schedules: this.scheduleSvc.get(true),
            periods: this.periodSvc.get(true),
            banks: this.bankAccountSvc.get<BankAccount>(true),
            outstanding: this.purchaseSvc.get<Txn>(false, new HttpParams().set('outstanding', '!=0.00',))
        }).pipe(map(result => {
            this.currentUser = result.currentUser;
            this.periods = result.periods as Period[];
            this.supplierNamePob.setOptions(result.suppliers, this.importDoc);
            this.bCodePOB.setOptions(result.bCodes, this.importDoc);
            this.bCodes = result.bCodes;
            this.schedulePOB.setOptions(result.schedules, this.importDoc);
            this.bankAccounts = result.banks;
            this.outstanding = result.outstanding
            return true;
        }));
    }

    validatePeriodOpen(field: ImportField) {
        const val = field.getValue();
        const prd = this.periods.find(p => p.to >= val && p.from <= val);
        if (!prd) {
            return $localize` There is no period for date ${val}`
        } if (prd.statusId !== Period.STATUS.OPEN.id) {
            return $localize` Period ${prd.name} is not open for transactions`;
        }
        return null;
    }

    parseRows(rows: unknown[][]): ImportDoc {
        this.gps.availableImportItems = this.availableImportItems;
        this.gps.parseRows(rows, this.importDoc);
        return this.importDoc;
    }

    getCreditKey(c: ItemInput) {
        return `${c.supplierId}-${c.txnDate}-${c.reference}`;
    }

    getCredit(c: ItemInput): CreditOutput {
        const key = this.getCreditKey(c);
        if (this.importCredits.has(key)) {
            return this.importCredits.get(key)
        }
        const co = {
            supplierId: c.supplierId,
            //typeId: c.typeId,
            txnDate: c.txnDate,
            reference: c.reference,
            notes: c.notes,
            debit: 0,
            lineItems: [],
            outstanding: 0,
            svcFrom: c.svcFrom,
            svcTo: c.svcTo,
        }
        this.importCredits.set(key, co);
        return co;
    }

    processCredit(c: ItemInput, line: ImportRow) {
        if (c.lineAmount > 0) {
            line.addError('Credit should be a negative amount');
        }
        const co = this.getCredit(c);
        co.lineItems.push({
            bCodeId: c.bCodeId,
            scheduleId: c.scheduleId,
            credit: 0 - c.lineAmount,
            notes: c.notes
        })
        co.debit -= c.lineAmount;
        co.outstanding += c.lineAmount;
        const bc = this.bCodes.find(b => b.id === c.bCodeId);
        if (bc.prepaid) {
            if (!co.svcFrom || !co.svcTo) {
                line.addError(`Prepaid entries ${c.reference} must have service dates`);
            }
        }
        if (co.svcFrom && co.svcFrom !== c.svcFrom) {
            line.addError(`Service From date ${c.svcFrom} does not match existing credit ${co.svcFrom}`);
        }
        co.svcFrom = c.svcFrom;
        if (co.svcTo && co.svcTo !== c.svcTo) {
            line.addError(`Service To date ${c.svcTo} does not match existing credit ${co.svcTo}`);
        }
        if (
            (co.svcFrom === null && co.svcTo !== null) || // svcFrom is null, svcTo is not
            (co.svcFrom !== null && co.svcTo === null) || // svcTo is null, svcFrom is not
            (co.svcFrom !== null && co.svcTo !== null && co.svcTo < co.svcFrom) // Both exist, but svcTo is not greater than svcFrom
        ) {
            line.addError(`Service dates ${c.svcFrom} -> ${c.svcTo} invalid`);
        }
        co.svcTo = c.svcTo;
    }

    processPayment(c: ItemInput, line: ImportRow) {
        let hasAcct = false;
        let bank: BankAccount;

        if (c.bCodeId) {
            hasAcct = true;
            bank = this.bankAccounts.find(ba => ba.bCodeId === c.bCodeId);
        }

        if (hasAcct && !bank) {
            line.addError(`Account is not a bank account not found`);
        }
        const payment = {
            supplierId: c.supplierId,
            //typeId: c.typeId,
            txnDate: c.txnDate,
            reference: c.reference,
            notes: c.notes,
            amount: c.lineAmount
        } as PaymentOutput;

        if (bank) {
            payment.bankId = bank.id;
        }
        this.matchPurchase(payment, line);
    }

    getPurchaseKey(c: ItemInput) {
        return `${c.supplierId}-${c.txnDate}-${c.reference}`;
    }

    getPurchase(c: ItemInput) : PurchaseOutput {
        const key = this.getPurchaseKey(c);
        if (this.importPurchases.has(key)) {
            return this.importPurchases.get(key)
        }
        const co = {
            supplierId: c.supplierId,
            //typeId: c.typeId,
            txnDate: c.txnDate,
            reference: c.reference,
            notes: c.notes,
            credit: 0,
            lineItems: [],
            outstanding: 0,
            svcFrom: c.svcFrom,
            svcTo: c.svcTo
        }
        this.importPurchases.set(key, co);
        return co;
    }

    processPurchase(c: ItemInput, line: ImportRow) {
        if (c.lineAmount < 0) {
            line.addError('Purchase should be an amount greater than zero');
        }
        const co = this.getPurchase(c);
        co.lineItems.push({
            bCodeId: c.bCodeId,
            scheduleId: c.scheduleId,
            debit: c.lineAmount,
            notes: c.notes
        })
        co.credit += c.lineAmount;
        co.outstanding += c.lineAmount;

        const bc = this.bCodes.find(b => b.id === c.bCodeId);
        if (!bc) {
            line.addError(`Budget Code Missing`);
        } else if (bc.prepaid) {
            if (!co.svcFrom || !co.svcTo) {
                line.addError(`Prepaid entries ${c.reference} must have service dates`);
            }
        }
        if (co.svcFrom && co.svcFrom !== c.svcFrom) {
            line.addError(`Service From date ${c.svcFrom} does not match existing invoice ${co.svcFrom}`);
        }
        co.svcFrom = c.svcFrom;
        if (co.svcTo && co.svcTo !== c.svcTo) {
            line.addError(`Service To date ${c.svcTo} does not match existing invoice ${co.svcTo}`);
        }
        if (
            (co.svcFrom === null && co.svcTo !== null) || // svcFrom is null, svcTo is not
            (co.svcFrom !== null && co.svcTo === null) || // svcTo is null, svcFrom is not
            (co.svcFrom !== null && co.svcTo !== null && co.svcTo < co.svcFrom) // Both exist, but svcTo is not greater than svcFrom
        ) {
            line.addError(`Service dates ${c.svcFrom} -> ${c.svcTo} invalid`);
        }
        co.svcTo = c.svcTo;
    }

    matchImportInvoice(pay: PaymentOutput, line: ImportRow) {
        let matched = false;
        for (const purchase of this.importPurchases.values()) {
            // Payment against an invoices is a negative amount
            if (purchase.supplierId === pay.supplierId && purchase.reference === pay.reference && pay.amount <= 0) {
                matched = true;
                if (NrHelper.toCurrency(purchase.outstanding) < NrHelper.toCurrency(0 - pay.amount)) {
                    line.addError(`Overpays invoice ${purchase.supplierId} ${purchase.reference} ${purchase.credit} which has ${purchase.outstanding} outstanding`);
                } else {
                    purchase.outstanding += pay.amount;// pay.amount negative for a payment, so outstanding reduced!
                }
            }
        }
        return matched;
    }

    matchImportCredit(pay: PaymentOutput, line: ImportRow) {
        let matched = false;
        const credits = this.importCredits.values();
        console.log('Trying to match pay', {pay, credits })
        for (const credit of credits) {
            // Payment against a credit (aka refund refund received) is a postive amount
            if (credit.supplierId === pay.supplierId && credit.reference === pay.reference && pay.amount >= 0) {
                matched = true;
                if (NrHelper.toCurrency(credit.outstanding) > NrHelper.toCurrency(0 - pay.amount)) {
                    line.addError(`Over refunds credit note ${credit.supplierId} ${credit.reference} ${credit.debit} which has ${0 - credit.outstanding} outstanding`);
                } else {
                    credit.outstanding += pay.amount; // pay.amount of refund is positive, outstanding amount of credit is negative!
                }
            }
        }
        return matched;
    }
    
    matchExistingInvoice(pay: PaymentOutput, line: ImportRow) {
        let matched = false;
        const prevTxn = this.outstanding.find( o => o.supplierId === pay.supplierId && o.reference === pay.reference);
        if (prevTxn) {
            matched = true;
            if (NrHelper.toCurrency(prevTxn.outstanding) < NrHelper.toCurrency(0 - pay.amount)) {
                line.addError(`Overpays invoice ${prevTxn.supplierId} ${prevTxn.reference} ${prevTxn.credit} which has ${prevTxn.outstanding} outstanding`);
            } else {
                prevTxn.outstanding += pay.amount;// pay.amount negative for a payment, so outstanding reduced!
            }            
        }
        return matched;
    }

    matchPurchase(pay: PaymentOutput, line: ImportRow): void {
        // Match against an invoice in this file.
        if (this.matchImportInvoice(pay, line) || this.matchImportCredit(pay, line) ||this.matchExistingInvoice(pay, line)) {
            this.importPayments.push(pay);
        } else {
            line.addError('No matching purchase found - Cannot load payment with corresponding invoice/credit');
        }
    }

    validateData(postItems: ItemInput[]) {
        const dataRows = this.importDoc.getGeneralRows();
        console.log({dataRows, postItems});
        let paymentCnt = 0;
        let negativePayments = 0;

        for (let i = 0; i < postItems.length; i++) {
            const line = dataRows[i];
            const data = postItems[i];
            if (data.lineAmount === null || data.lineAmount === undefined) {
                line.addError('No line amount specified');
            }
            if (!data.txnDate) {
                line.addError('Transaction date is required - Ensure you have a valid date value in a column mapped to Transaction Date');
            }
            if (data.typeId === 'Purchase') {
                this.processPurchase(data, line);
            } else if (data.typeId === 'PurchaseCredit') {
                if (data['amount'] > 0) {
                    line.addError('Credits should be entered as a negative amount');
                }
                this.processCredit(data, line);
            } else if (data.typeId === 'Payment') {
                paymentCnt += 1;
                if (data['amount'] > 0) {
                    negativePayments += 1;
                }
                this.processPayment(data, line);
            } else {
                line.addError('Do not know what type of transaction this is');
            }
            if (!data.scheduleId) {
                line.addError('Needs a schedule to be assigned');
            }
        }

        if (negativePayments > (paymentCnt * .8)) {
            const msg = `'More than 80% of payments are positive.
            Normal payments should be entered as negative figures, to reduce the supplier balance outstanding.
            There are some exceptional cases, where you may need to record a positive payment,
            that increases the supplier balance such as a refund received from a supplier, but this is unusual.
            We assume if more than 80% appear to be refunds, you are accidentally importing your receipts as positive amounts! `;
            this.importDoc.addNote(msg, null, true);
        }
        this.importDoc.finalise();
    }


    postToServer(): Observable<AbstractObjectList> {
        const postItems = this.gps.getItemsToPost() as unknown as ItemInput[];
        console.log(postItems);
        this.validateData(postItems);

        if (this.importDoc.hasErrors()) {
            return of(null);
        }


        const postData : ApImports = {
            purchases: [],
            credits: [],
            payments: [],
        }

        postData.purchases = Array.from(this.importPurchases.values()).sort((a,b) => a.txnDate.localeCompare(b.txnDate));
        postData.credits = Array.from(this.importCredits.values()).sort((a, b) => a.txnDate.localeCompare(b.txnDate));
        postData.payments = this.importPayments.sort((a, b) => a.txnDate.localeCompare(b.txnDate));

        console.log(postData);

        const url = TenantService.ajaxPath + 'importFile/' + this.batchType;
        const http = MyInjector.instance.get(HttpClient);
        return http.post<AbstractObjectList>(url, postData);

    }
}
